<template>
  <popup-dialog
    :visible="item != null"
    :reserve-sidebar="true"
    :backdrop="false"
    :close-button-visible="false"
    :resizable="true"
    class="info-panel"
    :class="{ '-side-image': !!$slots['image'] }"
    :min-width="400"
    x-alignment="center"
    y-alignment="center"
    @close="emit('close')"
  >
    <template #header>
      <header class="info-panel__header">
        <button
          v-if="backVisible"
          class="button-icon info-panel__backbutton"
          type="button"
          @click="emit('back')"
        >
          <lucide-icon name="chevron-left" />
        </button>

        <div v-if="$slots['header-image']" class="info-panel__headerimage">
          <slot name="header-image"></slot>
        </div>

        <div class="info-panel__headertitle">
          <span class="info-panel__type">
            <template v-if="label">{{ label }}</template>
            <template v-else>
              {{ $t(`objectNames.${item?.type}`) }}
            </template>
          </span>
          <div class="info-panel__titleline">
            <h1 class="info-panel__title title">
              <template v-if="title">{{ title }}</template>
              <i v-else class="info-panel__titleplaceholder">{{ $t("labels.unnamed") }}</i>
            </h1>
          </div>
        </div>

        <div v-if="$slots['header']" class="info-panel__headerstate">
          <slot name="header"></slot>
        </div>
        
        <button
          class="button-icon info-panel__closebutton"
          type="button"
          @click.prevent="emit('close')"
        >
          <lucide-icon name="x" />
        </button>
        <template v-if="!loadedModel?.loadedModel?.infoPopupsLocked">
          <button
            class="button-icon info-panel__sectionsbutton"
            @click="toggleSections"
          >
            <lucide-icon :name="sectionsVisible ? 'chevron-right' : 'menu'" />
          </button>
        </template>
      </header>
    </template>
    <template #default="{ width, height }">
      <div
        class="info-panel__main"
        :class="{
          '-has-image': !!$slots['image'],
          '-sections-open': sectionsActuallyVisible
        }"
      >
        <slot name="image"></slot>

        <div class="info-panel__content">
          <transition-group name="info-panel__sections-transition">
            <section
              v-for="section in visibleSections"
              :key="section"
              :class="{ '-hidden': $slots[section] == null }"
              class="info-panel__section"
            >
              <h3 class="info-panel__subheading">
                <slot :name="`heading-${section}`">
                  <slot :name="`label-${section}`"></slot>
                </slot>
              </h3>
              <slot :name="section" :width="width - (7 *  FONT_SIZE) - 3" :height="height"></slot>
            </section>
          </transition-group>
        </div>

        <transition
          name="info-panel__sectionsmenu-transition"
          @after-leave="sectionsActuallyVisible = false"
        >
          <div v-show="sectionsVisible" class="info-panel__sections">
            <input-checkbox
              v-for="(visible, section) in sections"
              :key="section"
              :value="sections[sectionGroups[section]] && visible"
              class="mt-0"
              :class="{ 'info-panel__subsection': String(section).startsWith('_') }"
              @update:value="setSection(String(section), $event)"
            >
              <template #label>
                <slot :name="`label-${section}`">{{ section }}</slot>
              </template>
            </input-checkbox>

            <hr v-if="extraSettings" />

            <template v-for="(value, setting) in extraSettings" :key="setting">
              <template v-if="value.type === 'check'">
                <input-checkbox
                  :value="value.value"
                  class="mt-0"
                  @update:value="updateSetting(setting, $event)"
                >
                  <template #label>
                    <slot :name="`label-${setting}`">{{ setting }}</slot>
                  </template>
                </input-checkbox>
              </template>
              <template v-else-if="value.type === 'dropdown'">
                <input-dropdown
                  :options="value.options"
                  :value="value.value"
                  @update:value="updateSetting(setting, $event as string)"
                >
                  <template #label>
                    <slot :name="`label-${setting}`">{{ setting }}</slot>
                  </template>
                  <template #default="{ item }">
                    <slot :name="`option-${setting}-${item}`">{{ item }}</slot>
                  </template>
                </input-dropdown>
              </template>
            </template>
          </div>
        </transition>
      </div>
    </template>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import PopupDialog from "@core/ui/PopupDialog.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import type { InfoPanelSetting } from "@editor/store/infoPanels.store";

const FONT_SIZE = 14;

interface Props {
  title?: string;
  item: { type: string } | null;
  label?: string;
  sections: { [section: string]: boolean };
  extraSettings: { [setting: string]: InfoPanelSetting };
  backVisible?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  title: "",
  item: null,
  label: "",
  backVisible: false
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "back"): void;
  (e: "update:sections", value: { [section: string]: boolean }): void;
  (
    e: "update:extraSettings",
    value: { [setting: string]: InfoPanelSetting }
  ): void;
}>();

const loadedModel = useLoadedModel();
const sectionsVisible = ref(false);
const sectionsActuallyVisible = ref(false);

const visibleSections = computed(() =>
  Object.keys(props.sections).filter(
    (section) => (!section.startsWith("_")) && props.sections[section]
  )
);

const sectionGroups = computed(() => {
  const groups: { [key: string]: string } = {};
  let lastGroup = null;
  for (const section in props.sections) {
    if (section.startsWith("_")) {
      if (lastGroup == null) {
        console.warn("no group for section", section);
        groups[section] = section;
        continue;
      }
      groups[section] = lastGroup;
    } else {
      groups[section] = section;
      lastGroup = section;
    }
  }
  return groups;
});

function toggleSections() {
  sectionsVisible.value = !sectionsVisible.value;
  if (sectionsVisible.value) {
    sectionsActuallyVisible.value = true;
  }
}

function setSection(section: string, value: boolean) {
  
  const sections = {
    ...props.sections
  };
  
  const group = sectionGroups.value[section];
  if (value == true && group != section && sections[group] == false) {
    // set all other sub-sections to false
    for (const s in sectionGroups.value) {
      if (sectionGroups.value[s] == group) {
        sections[s] = false;
      }
    }
    sections[group] = true;
  }

  emit("update:sections", {
    ...sections,
    [section]: value
  });
}

function updateSetting<K extends number | string>(
  key: K,
  value: (typeof props.extraSettings)[K]["value"]
) {
  emit("update:extraSettings", {
    ...props.extraSettings,
    [key]: {
      ...props.extraSettings[key],
      value
    }
  });
}
</script>

<style lang="scss">
.info-panel {
  width: 800px;
  height: 600px;
  border-radius: 1rem;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto minmax(0, 1fr);

  &.-side-image {
    width: 1000px;
  }

  &__header {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      "headerimage title state closebutton"
      "headerimage title state sectionsbutton";
    padding-left: 2.5rem;
    padding-right: 2rem;
    padding-top: 1.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid var(--color-border-light);
    position: relative;
    gap: 0;
    background-color: var(--color-background-shallow);
  }

  &__headerimage {
    grid-area: headerimage;
    flex-shrink: 0;
    position: relative;
    height: 5.25rem;
    max-width: 15rem;
    padding-right: 1.25rem;

    img {
      display: block;
      height: 100%;
      width: auto;
      max-width: 10rem;
    }
  }

  &__headertitle {
    grid-area: title;
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-top: 0.25rem;
    padding-right: 1rem;
  }

  &__headerstate {
    grid-area: state;
    padding-top: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-left: 1px solid var(--color-border-light);
    border-right: 1px solid var(--color-border-light);
    padding-inline: 1rem;
  }

  &__closebutton,
  &__sectionsbutton {
    font-size: 1.5em;
    padding: 1rem;
    margin-right: -1.25rem;
    margin-left: 0rem;
    height: 3.5rem;
    line-height: 1em;
  }

  &__closebutton {
    grid-area: closebutton;
    align-self: start;
    margin-top: -1.25rem;
    margin-bottom: -0.5rem;
  }
  &__sectionsbutton {
    grid-area: sectionsbutton;
    align-self: end;
    margin-bottom: -1.25rem;
    margin-top: -0.5rem;
  }

  &__main {
    padding-left: 2.5rem;
    padding-top: 1.5rem;
    padding-right: 2rem;
    padding-bottom: 2rem;
    position: relative;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    gap: 1.5rem;
    overflow: hidden;

    &.-has-image {
      grid-template-columns: 322px 1fr;
    }
    &.-sections-open {
      grid-template-columns: 1fr 15rem;
    }
    &.-has-image.-sections-open {
      grid-template-columns: 322px 1fr 15rem;
    }

    & > img {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    position: relative;
    padding: 0 2rem;
    padding-bottom: 2rem;
    padding-left: 0.5rem;
    margin-left: -0.5rem;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  &__type {
    color: var(--color-text-medium);
    font-size: var(--font-size-label);
    font-weight: 400;
    margin: 0;
    padding: 0;
  }

  &__backbutton {
    position: absolute;
    top: 1rem;
    left: 0;
    font-size: 1.5rem;
  }

  &__icon {
    font-size: 1.5em;
    display: block;
    position: absolute;
    left: 2rem;
    top: 3rem;
    color: var(--color-text-lighter);
  }

  &__title {
    margin: 0;
    margin-top: 0.25rem;
    align-self: flex-start;
  }
  &__titleplaceholder {
    color: var(--color-text-medium);
    font-weight: normal;
  }

  &__titleline {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }

  .popup__close {
    top: 0rem;
  }

  &__imagetext {
    display: grid;
    grid-gap: 2rem;

    grid-template-columns: 322px 2fr;

    & > *:first-child:last-child {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  }

  &__description-image {
    display: block;
    position: relative;
    min-height: 20rem;
    width: 100%;
  }

  &__subheading {
    color: var(--color-text-medium);
    font-size: 16px;
    margin-bottom: 0.5rem;
    margin-top: 0;
    padding-top: 0;
  }

  &__section:not(.-hidden) ~ &__section {
    padding-top: 0.75rem;
    border-top: 1px solid var(--color-border-light);
  }

  &__section.-hidden {
    display: none;
  }

  &__sections {
    background-color: var(--color-background-deep);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    padding-right: 0;
    padding-bottom: 1rem;
    padding-top: 0.25rem;
    border-left: 1px solid var(--color-grey);
    overflow-x: hidden;
    overflow-y: auto;
    border-left: 2px solid var(--color-grey-light);
  }

  &__subsection {
    position: relative;
    padding-left: 2.25rem;
    gap: 0.5rem;

    .checkbox__graphic {
      height: 18px;
      width: 18px;
    }
    &::before,
    &::after {
      content: "";
      display: block;
      position: absolute;
      background-color: var(--color-border);
    }
    &::before {
      left: 0.75rem;
      top: 0.5rem;
      width: 1rem;
      height: 1px;
    }
    &::after {
      top: 0;
      left: 0.75rem;
      height: 0.5rem;
      width: 1px;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    display: grid;
    grid-template-columns: max-content auto;
    column-gap: 0.75rem;
    row-gap: 0.25rem;
    padding-block: 0.25rem;
    align-items: center;

    & > *:nth-child(odd) {
      align-self: start;
    }
  }

  &__expression {
    width: fit-content;
    max-width: 100%;
    padding: 0.5rem 0.25rem;
    background-color: rgba(var(--tint-contrast), 0.03);// var(--color-background);
    // box-shadow: inset 0 0 0 1px var(--color-border), inset 0 0 5px rgba(var(--tint-contrast), 0.1);
  }

  &__image-full {
    object-fit: contain;
    object-position: top center;
  }
  &__image-cut {
    object-fit: cover;
    object-position: center;
  }

  &__sections-transition-enter-active,
  &__sections-transition-leave-active {
    transition:
      transform 300ms ease-out,
      opacity 300ms ease-out;
  }

  &__sections-transition-enter-from,
  &__sections-transition-leave-to {
    transform: translateX(3rem);
    opacity: 0;
  }

  &__sections-transition-enter-to,
  &__sections-transition-leave-from {
    transform: translateX(0);
    opacity: 1;
  }

  &__sectionsmenu-transition-enter-active,
  &__sectionsmenu-transition-leave-active {
    transition: opacity 150ms;
  }

  &__sectionsmenu-transition-enter-from,
  &__sectionsmenu-transition-leave-to {
    opacity: 0;
  }

  &__sectionsmenu-transition-enter-to,
  &__sectionsmenu-transition-leave-from {
    opacity: 1;
  }
}
</style>
